import { TanStackDevtools } from "@tanstack/react-devtools";
import { FormDevtoolsPanel } from "@tanstack/react-form-devtools";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, Link, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TableDevtoolsPanel } from "@tanstack/react-table-devtools";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ErrorFallback } from "@/components/fallbacks";
import { Button, buttonVariants } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner.tsx";
import { useSyncLocales } from "@/hooks/use-sync-locales";
import { syncLocales } from "@/lib/utils";
import { ThemeProvider } from "@/providers/theme-provider.tsx";

type MyRouterContext = {
	queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
	beforeLoad: () => {
		syncLocales(i18next.language);
	},
	component: function Component() {
		return (
			<RootLayout>
				<Outlet />
			</RootLayout>
		);
	},
	errorComponent: function Component({ error, reset }) {
		const { t } = useTranslation();

		return (
			<RootLayout className="flex h-screen items-center justify-center">
				<ErrorFallback title={t(($) => $.common.status.pageError.title)}>
					<p className="max-w-md text-center text-muted-foreground">
						{error.message || t(($) => $.common.status.pageError.message)}
					</p>

					<div className="flex flex-col items-center justify-center">
						<Button className="mt-4" onClick={() => reset()} variant={"secondary"}>
							{t(($) => $.common.status.pageError.action)}
						</Button>

						<Link
							className={buttonVariants({
								className: "mt-4",
							})}
							to={"/"}
						>
							{t(($) => $.common.status.pageNotFound.action)}
						</Link>
					</div>
				</ErrorFallback>
			</RootLayout>
		);
	},
	notFoundComponent: function Component() {
		const { t } = useTranslation();

		return (
			<RootLayout className="flex h-screen items-center justify-center">
				<ErrorFallback title={t(($) => $.common.status.pageNotFound.title)}>
					<p className="max-w-md text-center text-muted-foreground">{t(($) => $.common.status.pageNotFound.message)}</p>

					<Link
						className={buttonVariants({
							className: "mt-4",
						})}
						to={"/"}
					>
						{t(($) => $.common.status.pageNotFound.action)}
					</Link>
				</ErrorFallback>
			</RootLayout>
		);
	},
});

function RootLayout({ children, className }: { children: React.ReactNode; className?: string }) {
	useSyncLocales();

	return (
		<div className={className}>
			<ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
				{children}

				<Toaster />

				<TanStackDevtools
					plugins={[
						{
							name: "TanStack Query",
							render: <ReactQueryDevtoolsPanel />,
						},
						{
							name: "TanStack Router",
							render: <TanStackRouterDevtoolsPanel />,
						},
						{
							name: "TanStack Form",
							render: <FormDevtoolsPanel />,
						},
						{
							name: "TanStack Table",
							render: <TableDevtoolsPanel />,
						},
					]}
				/>
			</ThemeProvider>
		</div>
	);
}
